// Detect runs of citation links in an executive-summary markdown body and
// collapse long runs into an inline placeholder the markdown renderer can
// hand off to React.
//
// The stitcher on the server emits citation tokens as literal markdown
// links of the form `[[N]](#exec-cite-N)`. We detect 2+ such links
// separated only by inline whitespace; runs whose length exceeds the
// configured threshold are rewritten to:
//
//     [CITATION-RUN:1,2,3,5,7](#exec-cite-run)
//
// The `<a>` renderer override recognizes this special href and renders a
// <CitationRun> component instead of a plain anchor. Shorter runs are
// passed through unchanged so every citation below the threshold keeps its
// current inline behavior.

import { CITATION_INLINE_LIMIT } from './constants';

// Matches a single citation link literal. The inner `[N]` is preserved by
// the stitcher, and the href is always `#exec-cite-N` for the same N.
const CITATION_LINK_RE = /\[\[(\d+)\]\]\(#exec-cite-\1\)/g;

// Matches a run of 2+ citation links separated only by inline whitespace.
// A run terminates on newlines or any other character; we do not cross
// paragraph or list-item boundaries.
const CITATION_RUN_RE = /(\[\[(\d+)\]\]\(#exec-cite-\2\))(?:[ \t]+\[\[(\d+)\]\]\(#exec-cite-\3\))+/g;

export const CITATION_RUN_HREF = '#exec-cite-run';

export interface ParseResult {
  markdown: string;
  /**
   * Run index -> ordered list of citation numbers. The index matches the
   * N-th CITATION-RUN placeholder in the rewritten markdown so the renderer
   * can pluck the right numbers out without re-parsing.
   *
   * Concretely: the first placeholder encountered during rendering resolves
   * to `runs[0]`, the second to `runs[1]`, and so on.
   */
  runs: number[][];
}

/**
 * Rewrite long citation runs in the body. Returns the rewritten markdown
 * plus an ordered array of the per-run citation numbers. Runs shorter than
 * the threshold are left untouched.
 */
export function parseCitationRuns(markdown: string): ParseResult {
  const runs: number[][] = [];

  const rewritten = markdown.replace(CITATION_RUN_RE, (match) => {
    const numbers: number[] = [];
    for (const m of match.matchAll(CITATION_LINK_RE)) {
      numbers.push(Number(m[1]));
    }
    // Reset the shared regex state since matchAll on the same regex mutates it.
    CITATION_LINK_RE.lastIndex = 0;

    if (numbers.length <= CITATION_INLINE_LIMIT) {
      // Below threshold: leave the run intact.
      return match;
    }

    const runIndex = runs.length;
    runs.push(numbers);
    // Anchor text uses `-` not `:` to keep the `N:M` shape away from
    // remark-directive, which rewrites `:digit` into a text directive and
    // would otherwise split the placeholder text mid-link (see the
    // analogous preprocessMarkdownForCustomComponents guard in
    // packages/chat-components/.../Markdown.tsx for the {{type:id}} case).
    return `[CITATION-RUN-${runIndex}](${CITATION_RUN_HREF})`;
  });

  return { markdown: rewritten, runs };
}

/**
 * Returns the run index encoded in a placeholder anchor's text, or null
 * if the anchor is not a citation-run placeholder.
 */
export function extractRunIndex(anchorText: string): number | null {
  const m = /^CITATION-RUN-(\d+)$/.exec(anchorText);
  return m ? Number(m[1]) : null;
}

/**
 * Extract the citation number from an anchor href like `#exec-cite-7`.
 * Returns null if the href is not a single-citation link.
 */
export function extractCitationNumberFromHref(href: string | undefined): number | null {
  if (!href) return null;
  const m = /^#exec-cite-(\d+)$/.exec(href);
  return m ? Number(m[1]) : null;
}
